import React, { useState } from 'react';
import { observer } from 'mobx-react';
import jsPDF from 'jspdf';
import {
  Section, SectionTitle, ToggleButton, Placeholder
} from '../../pages/MyMeetingsPage.styles';
import {
  isTranscriptExpired, TRANSCRIPT_EXPIRED_MESSAGE
} from './transcriptRetention';

/**
 * Transcript download section.
 *
 * Fetches a presigned S3 URL via the backend, downloads the transcript JSON,
 * formats it as a paginated PDF, and triggers a browser download.
 *
 * The transcript JSON shape produced by transcription-parser.js / our seed
 * script is `{ transcript: "...", audio_segments: [...] }`. We render
 * `audio_segments` as speaker-attributed paragraphs when available, falling
 * back to the flat `transcript` string.
 */
const TranscriptSection = observer(({ meeting, myMeetingsStore, companyId }) => {
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);
  // Backstop for the lifecycle-lag window: the age gate below hides the button
  // at exactly 30 days, but if a request slips through (e.g. stale meetingDate)
  // the backend returns { expired: true } once the file is actually gone.
  const [expiredNotice, setExpiredNotice] = useState(false);

  if (meeting.status !== 'delivered') return null;

  // Transcripts are deleted by a 30-day S3 lifecycle rule (contractual); show the retention
  // notice instead of a download button that would fail.
  const expired = expiredNotice || isTranscriptExpired(meeting.meetingDate);

  const onDownload = async () => {
    setDownloading(true);
    setError(null);

    try {
      const result = await myMeetingsStore.fetchTranscriptUrl(
        companyId,
        meeting.botId
      );
      if (result.expired) {
        setExpiredNotice(true);
        return;
      }
      if (result.error) throw new Error(result.error);

      const res = await fetch(result.url);
      if (!res.ok) throw new Error(`Transcript fetch failed (${res.status})`);
      const data = await res.json();

      generatePdf(data, meeting);
    } catch (err) {
      console.error('Transcript download failed:', err);
      setError(err.message || 'Failed to download transcript');
    } finally {
      setDownloading(false);
    }
  };

  if (expired) {
    return (
      <Section>
        <SectionTitle>Transcript</SectionTitle>
        <Placeholder>{TRANSCRIPT_EXPIRED_MESSAGE}</Placeholder>
      </Section>
    );
  }

  return (
    <Section>
      <SectionTitle>Transcript</SectionTitle>
      <ToggleButton onClick={onDownload} disabled={downloading} type="button">
        {downloading ? 'Preparing PDF…' : 'Download transcript (PDF)'}
      </ToggleButton>
      {error && <Placeholder>Couldn't download: {error}</Placeholder>}
    </Section>
  );
});

function generatePdf(data, meeting) {
  const doc = new jsPDF({ unit: 'pt', format: 'letter' });
  const margin = 50;
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const usableWidth = pageWidth - 2 * margin;
  const lineHeight = 14;
  let y = margin;

  // ── Header ──
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text(meeting.meetingTitle || 'Meeting Transcript', margin, y);
  y += lineHeight + 6;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(120);
  const metaParts = [];
  if (meeting.meetingDate) metaParts.push(formatMeetingDate(meeting.meetingDate));
  if (meeting.duration) metaParts.push(formatDuration(meeting.duration));
  if (Array.isArray(meeting.participants) && meeting.participants.length > 0) {
    const names = meeting.participants
      .map(p => p.name || p.email)
      .filter(Boolean)
      .join(', ');
    if (names) metaParts.push(names);
  }
  if (metaParts.length > 0) {
    const metaLines = doc.splitTextToSize(metaParts.join(' · '), usableWidth);
    metaLines.forEach(line => {
      doc.text(line, margin, y);
      y += lineHeight - 2;
    });
  }
  y += 8;
  doc.setDrawColor(220);
  doc.line(margin, y, pageWidth - margin, y);
  y += lineHeight;
  doc.setTextColor(0);

  // ── Body: prefer structured audio_segments, fall back to flat transcript ──
  const segments = Array.isArray(data.audio_segments) ? data.audio_segments : null;

  const ensureSpace = (needed) => {
    if (y + needed > pageHeight - margin) {
      doc.addPage();
      y = margin;
    }
  };

  if (segments && segments.length > 0) {
    segments.forEach(seg => {
      const speaker = seg.speaker_label || seg.speaker || 'Speaker';
      const time = formatSegmentTime(seg.start_time);
      const text = (seg.transcript || seg.text || '').trim();
      if (!text) return;

      // Speaker line (bold)
      ensureSpace(lineHeight);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.text(`${speaker}${time ? ` (${time})` : ''}:`, margin, y);
      y += lineHeight;

      // Body text (wrapped)
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      const lines = doc.splitTextToSize(text, usableWidth);
      lines.forEach(line => {
        ensureSpace(lineHeight);
        doc.text(line, margin, y);
        y += lineHeight;
      });
      y += 6;
    });
  } else if (typeof data.transcript === 'string' && data.transcript) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    const lines = doc.splitTextToSize(data.transcript, usableWidth);
    lines.forEach(line => {
      ensureSpace(lineHeight);
      doc.text(line, margin, y);
      y += lineHeight;
    });
  } else {
    doc.setFont('helvetica', 'italic');
    doc.text('Transcript content was empty.', margin, y);
  }

  // ── Footer with page numbers ──
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(150);
    doc.text(
      `Page ${i} of ${pageCount}`,
      pageWidth - margin,
      pageHeight - 20,
      { align: 'right' }
    );
  }

  const slug = (meeting.meetingTitle || 'transcript')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 60);
  doc.save(`${slug || 'transcript'}.pdf`);
}

function formatMeetingDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit'
    });
  } catch { return iso; }
}

function formatDuration(seconds) {
  if (!seconds || seconds < 0) return '';
  const mins = Math.round(seconds / 60);
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function formatSegmentTime(seconds) {
  if (typeof seconds !== 'number' || !isFinite(seconds)) return '';
  const total = Math.round(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export default TranscriptSection;
